import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useImmer } from "use-immer";
import { AppConfig } from "../../AppConfig";
import { JWT_API_KEY, TRUE } from "../../Common/Constants";
import { GenerateHeaderInformation } from "../../Common/Helpers/ApiHelper";
import { AtlasFallbackDestination } from "../../Common/Helpers/LineItemHelper";
import { convertToErrorDetails, logError, returnJsonOrThrowError } from "../../Common/services/ErrorService";
import { fetchRetry } from "../../Common/services/FetchRetry";
import { processBundleItemsAndPaymentStatus } from "../../Common/services/QuoteService";
import {
  ApiRequestType,
  ErrorCode,
  ErrorDetails,
  Money,
  PaymentInfo,
  PaymentInfoStatus,
  Quote,
  QuotePaymentStatus,
  SegmentType,
} from "../../Common/Types";
import { useSessionContext } from "../SessionContext/SessionContext";
import { useDatasources } from "../StoryblokContext/StoryblokContext";

export type QuoteData = {
  rawQuoteData: Quote;
  quoteData: Quote;
  reservationPrice?: Money;
};

export const isQuotePreferred = (quote: QuoteData, preferredQuoteId?: string): boolean =>
  Boolean(preferredQuoteId && quote.quoteData.id === preferredQuoteId);

const applyPreferredQuoteToQuotes = (items: Array<QuoteData>, preferredQuoteId?: string): Array<QuoteData> =>
  items.map((quote) => {
    const isPreferredQuote = isQuotePreferred(quote, preferredQuoteId);
    return {
      ...quote,
      quoteData: { ...quote.quoteData, isPreferredQuote },
      rawQuoteData: { ...quote.rawQuoteData, isPreferredQuote },
    };
  });

export type QuoteModificationResponse = {
  quote?: Quote;
  isSuccessful: boolean;
  error?: ErrorDetails;
};

export type DestinationContent = {
  name: string;
  heroImage: string;
  schoolImage: string;
  accommodationCodes: string[];
};

export type QuotesContent = {
  destinations: Map<string, DestinationContent>;
};

interface QuotesContextValue {
  quotes: Array<QuoteData>;
  isQuoteListLoading: boolean;
  quotesContentCache: QuotesContent;
  isQuotesDestinationsLoading: boolean;
  selected?: QuoteData;
  setSelectedQuoteById: (x?: string) => void;
  addedQuote: (x: Quote) => void;
  replaceQuote: (x: QuoteData) => boolean;
  addPaymentToQuote: (x: Quote, payment: PaymentInfo) => boolean;
  sendQuoteEvent: (quoteId: string, eventName: string) => void;
  activeLanguage?: string;
  setupError?: ErrorDetails;
  preferredQuoteId?: string;
  setPreferredQuoteId: (id?: string) => void;
}

const initialValue: QuotesContextValue = {
  quotes: [],
  isQuoteListLoading: true,
  quotesContentCache: {
    destinations: new Map(),
  },
  isQuotesDestinationsLoading: true,
  setSelectedQuoteById: () => {},
  addedQuote: () => {},
  replaceQuote: () => true,
  addPaymentToQuote: () => true,
  sendQuoteEvent: () => {},
  preferredQuoteId: undefined,
  setPreferredQuoteId: () => {},
};

const QuoteContext = createContext<QuotesContextValue>(initialValue);

interface QuoteContextProviderProps {
  children: ReactNode;
}

const QuoteContextProvider: React.FC<QuoteContextProviderProps> = ({ children }) => {
  const { quoteId } = useParams();
  const { session } = useSessionContext();
  const { activeLanguage, atlasfallbackDestinationMappings, featureSettings } = useDatasources();
  const [quotes, setQuotes] = useState<Array<QuoteData>>([] as Array<QuoteData>);
  const [destinationsContent, setDestinationsContent] = useState<Map<string, DestinationContent>>(new Map());
  const [selected, updateSelected] = useImmer<QuoteData | undefined>(undefined);
  const [isQuoteListLoading, setIsQuoteListLoading] = useState(true);
  const [isQuotesDestinationsLoading, setIsQuotesDestinationsLoading] = useState(true);
  const [setupError, setSetupError] = useState<ErrorDetails | undefined>(undefined);
  const [preferredQuoteId, setPreferredQuoteIdState] = useState<string | undefined>(undefined);
  const preferredQuoteIdRef = useRef<string | undefined>(undefined);
  const hasHydratedPreferredQuoteFromApi = useRef(false);
  const isPreferredQuoteEnabled = featureSettings.isPreferredQuoteEnabled === TRUE;

  const setSelectedQuoteById = (id?: string) => {
    if (!quotes || quotes.length === 0) {
      return;
    }
    if (!id) {
      updateSelected(undefined);
      return;
    }
    const matchedQuote = quotes.find((q) => q.quoteData.id === id);
    updateSelected(matchedQuote || undefined);
    setSetupError(
      !matchedQuote
        ? {
            details: {
              fallbackMessage: "Requested quote could not be found on the list of customer's quotes.",
              additionalDetails: `Quote id: ${id} (was it published?)`,
            },
            code: ErrorCode.LoadedQuoteNotListed,
          }
        : undefined
    );
  };

  const quotesContent = {
    destinations: destinationsContent,
  };

  const applyPreferredQuoteLocally = (id?: string) => {
    preferredQuoteIdRef.current = id;
    setPreferredQuoteIdState(id);
    setQuotes((currentQuotes) => applyPreferredQuoteToQuotes(currentQuotes, id));
  };

  const updatePreferredQuote = (id?: string) => {
    const previousPreferredQuoteId = preferredQuoteId;

    if (id === previousPreferredQuoteId) {
      return;
    }

    // Lock hydration so a late GET /quotes response cannot revert user's choice.
    hasHydratedPreferredQuoteFromApi.current = true;
    applyPreferredQuoteLocally(id);
    if (!id) {
      return;
    }

    const url = `${AppConfig.api.quotation}/quotes/${id}`;
    const body = { isPreferredQuote: true };

    fetchRetry(url, {
      method: ApiRequestType.PATCH,
      headers: GenerateHeaderInformation(session.jwtAccessToken, JWT_API_KEY),
      body: JSON.stringify(body),
    })
      .then(async (response) => {
        if (!response.ok) {
          await returnJsonOrThrowError<void>(response);
          return;
        }

        await response.text();
      })
      .catch((error) => {
        applyPreferredQuoteLocally(previousPreferredQuoteId);
        const errorDetails = convertToErrorDetails(error, ErrorCode.QuotesLoadingFailed, {
          fallbackMessage: "Could not save preferred quote.",
          additionalDetails: `Quote id: ${id}. Tracking id: ${session.opportunity.id}`,
        });
        logError(errorDetails);
      });
  };

  useEffect(() => {
    if (quotes && quoteId) setSelectedQuoteById(quoteId);
  }, [quoteId, quotes]);

  useEffect(() => {
    // get quotes for opportunity
    if (session?.opportunity?.id) {
      hasHydratedPreferredQuoteFromApi.current = false;
      preferredQuoteIdRef.current = undefined;
      setPreferredQuoteIdState(undefined);
      setIsQuoteListLoading(true);
      const quotesListUrl = `${AppConfig.api.quotation}/quotes?opportunityId=${session.opportunity.id}&offset=${0}&limit=${50}`;
      fetchRetry(quotesListUrl, {
        method: ApiRequestType.GET,
        headers: GenerateHeaderInformation(session.jwtAccessToken, JWT_API_KEY),
      })
        .then((response) => returnJsonOrThrowError<Array<Quote>>(response))
        .then((result) => {
          if (!result) return;
          let oppHasPaidQuote = result.some((x: Quote) => x.payments?.some((p) => p.status === PaymentInfoStatus.Paid));
          if (featureSettings.allowOnlyOnePaidQuote === "false") {
            oppHasPaidQuote = false;
          }
          //Remove all the bundled lineItems and add their price to the price of the course
          const items: Array<QuoteData> = result
            .filter((x: Quote) => x.isPublic)
            .map((q: Quote) => {
              return { quoteData: processBundleItemsAndPaymentStatus(q, oppHasPaidQuote), rawQuoteData: q };
            });
          setQuotes(() => {
            if (!hasHydratedPreferredQuoteFromApi.current) {
              const preferredFromRaw = items.find((quote) => quote.rawQuoteData.isPreferredQuote === true)?.quoteData
                .id;
              preferredQuoteIdRef.current = preferredFromRaw;
              setPreferredQuoteIdState(preferredFromRaw);
              hasHydratedPreferredQuoteFromApi.current = true;
              return items;
            }

            return applyPreferredQuoteToQuotes(items, preferredQuoteIdRef.current);
          });
          if (items.length == 0) {
            setSetupError({
              code: ErrorCode.NoQuotesLoaded,
              details: {
                additionalDetails: `Tracking id: ${session.opportunity.id}`,
              },
            });
          }
        })
        .catch((error) => {
          const errorDetails = convertToErrorDetails(error, ErrorCode.QuotesLoadingFailed, {
            fallbackMessage: `Could not load quotes`,
            additionalDetails: `Tracking id: ${session.opportunity.id}`,
          });
          logError(errorDetails);
          setSetupError(errorDetails);
          setQuotes([] as Array<QuoteData>);
        })
        .finally(() => {
          setIsQuoteListLoading(false);
        });
    }
  }, [session?.opportunity?.id]);

  useEffect(() => {
    if (!quotes || quotes.length === 0 || destinationsContent.size > 0) {
      return;
    }
    // get unique destinations for opportunity once quotes are loaded
    const uniqueDestinationCodes = quotes
      .map((quote) =>
        quote.quoteData.segments
          .filter((segment) => segment.type === SegmentType.Course) // Filter segments
          .map((segment) => segment.destinationCode)
      )
      .flat()
      .filter((val, ix, arr) => arr.indexOf(val) === ix);

    if (session.accessToken && uniqueDestinationCodes.length > 0) {
      setIsQuotesDestinationsLoading(true);
      Promise.allSettled(
        uniqueDestinationCodes.map((destination) =>
          fetchRetry(
            `${AppConfig.api.efPlanner}/destinations?destinationCode=${AtlasFallbackDestination(destination, atlasfallbackDestinationMappings).toLowerCase()}&language=${activeLanguage?.atlas}`,
            {
              method: ApiRequestType.GET,
              headers: GenerateHeaderInformation(session.accessToken),
            }
          ).then((response) => {
            if (response.ok) {
              return response.json();
            }
            throw new Error(`Error retrieving atlas information about destination ${destination}.`);
          })
        )
      )
        .then((result) => {
          const destinationsContent = new Map<string, DestinationContent>();
          result.forEach((result, index) => {
            if (result.status === "fulfilled") {
              const destinationCode = uniqueDestinationCodes[index];
              destinationsContent.set(destinationCode, result.value);
            }
          });
          setDestinationsContent(destinationsContent);
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch((_) => {
          // handle error, pending api response codes
        })
        .finally(() => setIsQuotesDestinationsLoading(false));
    }
  }, [quotes]);

  useEffect(() => {
    const asyncFn = async () => {
      const fields = "name,content.code,content.schools";
      const language = "en";
      const tags = "city";
      const response = await fetchRetry(
        `${AppConfig.api.martechApi}/content/atlas/destinations?fields=${fields}&language=${language}&tags=${tags}`,
        {
          method: ApiRequestType.GET,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        return;
      }
      const data = await response.json();

      const destinationsContentMap = new Map(destinationsContent);

      for (const val of data) {
        const { content } = val;
        const destinationContent = destinationsContentMap.get(content.code.toUpperCase());
        if (destinationContent) {
          destinationContent.schoolImage = content.schools[0].school_image.image;
          destinationsContentMap.set(content.code.toUpperCase(), destinationContent);
        }
      }
      setDestinationsContent(destinationsContentMap);
    };
    if (destinationsContent.size === 0) {
      return;
    }
    asyncFn();
  }, [destinationsContent.size]);

  const addedQuote = (quote: Quote): void => {
    quote.isPublic = true;
    setQuotes([...quotes, { quoteData: { ...quote, status: QuotePaymentStatus.Unreserved } } as unknown as QuoteData]);
  };

  const addPaymentToQuote = (x: Quote, payment: PaymentInfo) => {
    let updated = false;
    const updatedQuotes = quotes.map((q) => {
      if (q.quoteData.id === x.id) {
        updated = true;
        const old = q.quoteData.payments || [];
        old.push(payment);
        q.quoteData.payments = old;
        q.quoteData.status =
          payment.status === PaymentInfoStatus.Paid ? QuotePaymentStatus.Paid : QuotePaymentStatus.Reserved;
        return q;
      } else {
        if (![QuotePaymentStatus.Paid, QuotePaymentStatus.Reserved].some((x) => x === q.quoteData.status)) {
          q.quoteData.status = QuotePaymentStatus.Canceled;
        }
      }
      return q;
    });
    setQuotes(updatedQuotes);
    setSelectedQuoteById(x.id);
    return updated;
  };

  const sendQuoteEvent = (quoteId: string, eventName: string) => {
    fetchRetry(`${AppConfig.api.quotation}/api/v2/Quote/${quoteId}/events`, {
      method: ApiRequestType.POST,
      headers: GenerateHeaderInformation(session.jwtAccessToken, JWT_API_KEY),
      body: JSON.stringify({
        event: eventName,
        timestamp: new Date().toISOString(),
      }),
    })
      .then((response) => returnJsonOrThrowError(response))
      .catch(() => {});
  };

  const replaceQuote = (quoteData: QuoteData): boolean => {
    let updated = false;
    const updatedQuotes = quotes.map((q) => {
      if (q.quoteData.id === quoteData.quoteData.id) {
        updated = true;
        return applyPreferredQuoteToQuotes([quoteData], preferredQuoteIdRef.current)[0];
      }
      return q;
    });

    setQuotes(updatedQuotes);
    return updated;
  };

  return (
    <QuoteContext.Provider
      value={{
        quotes,
        isQuoteListLoading,
        quotesContentCache: quotesContent,
        isQuotesDestinationsLoading: isQuotesDestinationsLoading,
        selected,
        setSelectedQuoteById: setSelectedQuoteById,
        addedQuote: addedQuote,
        replaceQuote: replaceQuote,
        addPaymentToQuote: addPaymentToQuote,
        sendQuoteEvent: sendQuoteEvent,
        activeLanguage: activeLanguage?.efcom,
        setupError: setupError,
        preferredQuoteId: isPreferredQuoteEnabled ? preferredQuoteId : undefined,
        setPreferredQuoteId: isPreferredQuoteEnabled ? updatePreferredQuote : () => undefined,
      }}
    >
      {children}
    </QuoteContext.Provider>
  );
};

const useQuoteContext = () => {
  return useContext(QuoteContext);
};

export { QuoteContextProvider, useQuoteContext };
